import { useCallback, useRef, useState } from 'react';
import html2canvas from 'html2canvas';

interface ShareResultsOptions {
  score: number;
  totalQuestions: number;
  modeName: string;
  team: string | null;
  cardRef: React.RefObject<HTMLDivElement | null>;
}

export function useShareResults({
  score,
  totalQuestions,
  modeName,
  team,
  cardRef,
}: ShareResultsOptions) {
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [downloadState, setDownloadState] = useState<'idle' | 'loading' | 'done'>('idle');

  // --- Build sharable URL ---
  const buildShareUrl = useCallback(() => {
    const params = new URLSearchParams({
      score: String(score),
      total: String(totalQuestions),
      mode: modeName,
      ...(team ? { team } : {}),
    });
    return `${window.location.origin}/results?${params.toString()}`;
  }, [score, totalQuestions, modeName, team]);

  const shareText = `I scored ${score}/${totalQuestions} on The Quizzler (${modeName} mode)! Can you beat me?`;

  // --- Web Share API ---
  const handleNativeShare = useCallback(async () => {
    const url = buildShareUrl();
    if (navigator.share) {
      try {
        await navigator.share({ title: 'The Quizzler Results', text: shareText, url });
      } catch (err) {
        // User cancelled or API unavailable — fall back silently
      }
    }
  }, [buildShareUrl, shareText]);

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  // --- Copy link ---
  const handleCopyLink = useCallback(async () => {
    const url = buildShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    }
  }, [buildShareUrl]);

  // --- Download as image (html2canvas) ---
  const handleDownloadImage = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloadState('loading');

    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0d0820',
        scale: 2, // retina quality
        useCORS: true,
        logging: false,
      });

      const link = document.createElement('a');
      link.download = `quizology-results-${score}-${totalQuestions}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      setDownloadState('done');
      setTimeout(() => setDownloadState('idle'), 2000);
    } catch (err) {
      console.error('Failed to generate image:', err);
      setDownloadState('idle');
    }
  }, [cardRef, score, totalQuestions]);

  return {
    handleNativeShare,
    handleCopyLink,
    handleDownloadImage,
    copyState,
    downloadState,
    canNativeShare,
  };
}
